import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, Button, CircularProgress, InputBase } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const PAGE_PADDING = 16;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3.0;

const PDFViewer = ({ paperId, paperTitle, apiBaseUrl, source = 'unknown' }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [zoomInput, setZoomInput] = useState('100');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fitScaleToContainer = async (pdf) => {
    const container = containerRef.current;
    if (!pdf || !container) return;

    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 });
    const availableWidth = container.clientWidth - PAGE_PADDING * 2;
    if (availableWidth <= 0 || viewport.width <= 0) return;

    const fitted = Math.min(1, availableWidth / viewport.width);
    setScale(Math.min(Math.max(fitted, MIN_SCALE), MAX_SCALE));
  };

  useEffect(() => {
    if (paperId) {
      loadPDF();
    }
  }, [paperId]);

  useEffect(() => {
    if (pdfDoc) {
      renderPage();
    }
  }, [pdfDoc, currentPage, scale]);

  useEffect(() => {
    setZoomInput(String(Math.round(scale * 100)));
  }, [scale]);

  const loadPDF = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${apiBaseUrl}/api/papers/${paperId}/download-direct?track=false`;
      const loadingTask = pdfjsLib.getDocument(url);
      const pdf = await loadingTask.promise;
      
      await fitScaleToContainer(pdf);
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setCurrentPage(1);
      setLoading(false);
    } catch (err) {
      console.error('Error loading PDF:', err);
      setError('Failed to load PDF. It may not be available.');
      setLoading(false);
    }
  };

  const renderPage = async () => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale });
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
    } catch (err) {
      console.error('Error rendering page:', err);
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, MAX_SCALE));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, MIN_SCALE));
  };

  const applyZoomInput = () => {
    const parsed = parseInt(zoomInput, 10);
    if (Number.isNaN(parsed)) {
      setZoomInput(String(Math.round(scale * 100)));
      return;
    }
    setScale(Math.min(Math.max(parsed / 100, MIN_SCALE), MAX_SCALE));
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, numPages));
  };

  const handleDownload = () => {
    window.open(`${apiBaseUrl}/api/papers/${paperId}/download-direct?source=${source}`, '_blank');
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: '#f5f5f5'
    }}>
      {/* Toolbar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        p: 1, 
        borderBottom: '1px solid #ddd',
        bgcolor: 'white',
        gap: 1
      }}>
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }} noWrap>
          {paperTitle}
        </Typography>

        {!loading && !error && (
          <>
            <IconButton size="small" onClick={handlePrevPage} disabled={currentPage <= 1}>
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="caption">
              {currentPage} / {numPages}
            </Typography>
            <IconButton size="small" onClick={handleNextPage} disabled={currentPage >= numPages}>
              <NavigateNextIcon />
            </IconButton>

            <Box sx={{ borderLeft: '1px solid #ddd', height: 24, mx: 1 }} />

            <IconButton size="small" onClick={handleZoomOut} disabled={scale <= MIN_SCALE}>
              <ZoomOutIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InputBase
                value={zoomInput}
                onChange={(e) => setZoomInput(e.target.value.replace(/[^\d]/g, ''))}
                onBlur={applyZoomInput}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.target.blur();
                }}
                inputProps={{
                  'aria-label': 'Zoom',
                  inputMode: 'numeric',
                }}
                sx={{
                  width: 40,
                  fontSize: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  px: 0.5,
                  '& input': { textAlign: 'center', p: '2px 0' },
                }}
              />
              <Typography variant="caption">%</Typography>
            </Box>
            <IconButton size="small" onClick={handleZoomIn} disabled={scale >= MAX_SCALE}>
              <ZoomInIcon />
            </IconButton>

            <Box sx={{ borderLeft: '1px solid #ddd', height: 24, mx: 1 }} />

            <Button 
              size="small" 
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              variant="outlined"
            >
              Download
            </Button>
          </>
        )}
      </Box>

      {/* PDF Canvas */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          scrollbarGutter: 'stable',
          p: `${PAGE_PADDING}px`,
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>Loading PDF...</Typography>
          </Box>
        )}
        
        {error && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography color="error">{error}</Typography>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              The PDF may not be accessible in this environment.
            </Typography>
          </Box>
        )}
        
        {!loading && !error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              minWidth: 'min-content',
              width: '100%',
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: 'white',
                flexShrink: 0,
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PDFViewer;

